import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  initializeSnagWebSocket,
  clearSnagEvent,
} from "../../Redux/service/Snag/SnagWebSocket";
import "./LiveSnag.css";

/**
 * Safely resolve the toast function for a given type.
 * React-Toastify supports: toast(), toast.success(), toast.info(), toast.warn(), toast.error()
 * (not toast.warning, and "default" should call toast()).
 */
function getToastFn(type) {
  const normalized = (type || "default").toString().toLowerCase();

  switch (normalized) {
    case "success":
      return toast.success;
    case "info":
      return toast.info;
    case "warning": // app-level alias
    case "warn": // react-toastify method name
      return toast.warn;
    case "error":
      return toast.error;
    case "default":
    default:
      return toast; // default toast(message)
  }
}

export default function LiveSnag() {
  const dispatch = useDispatch();
  const { latestSnagEvent, eventType, isConnected, connectionError } = useSelector(
    (state) => state.snagWebSocket
  );

  const processedEventsRef = useRef(new Set());
  const initializationAttempted = useRef(false);

  // ✅ Initialize WebSocket connection on mount
  useEffect(() => {
    console.log("🚀 LiveSnag component mounted");

    const token = localStorage.getItem("token") || localStorage.getItem("authToken");

    if (!token) {
      console.error("❌ No authentication token found");
      return;
    }

    if (!initializationAttempted.current) {
      console.log("🔌 Initializing WebSocket connection...");
      initializationAttempted.current = true;
      dispatch(initializeSnagWebSocket(token));
    }

    return () => {
      console.log("🧹 LiveSnag component unmounting");
      // Keep connection alive for other components
      // dispatch(disconnectSnagWebSocket());
    };
  }, [dispatch]);

  // ✅ Monitor connection status
  useEffect(() => {
    if (isConnected) {
      console.log("✅ WebSocket connected successfully");
    } else {
      console.log("⏳ WebSocket not connected yet...");
    }

    if (connectionError) {
      console.error("❌ WebSocket connection error:", connectionError);
    }
  }, [isConnected, connectionError]);

  // ✅ Handle incoming snag events
  useEffect(() => {
    if (!latestSnagEvent || !eventType) return;

    console.log("📨 Processing snag event:", {
      eventType,
      snagNumber: latestSnagEvent.snag_number,
      id: latestSnagEvent.id,
    });

    // ✅ Use a stable identifier so "already processed" can actually work.
    // (If you include Date.now(), every eventIdentifier is always unique.)
    const eventIdentifier = `${latestSnagEvent.id}-${eventType}-${latestSnagEvent.snag_number}`;

    if (processedEventsRef.current.has(eventIdentifier)) {
      console.log("⚠️ Event already processed, skipping:", eventIdentifier);
      dispatch(clearSnagEvent());
      return;
    }

    processedEventsRef.current.add(eventIdentifier);

    // Keep only last 100 identifiers
    if (processedEventsRef.current.size > 100) {
      const arr = Array.from(processedEventsRef.current);
      processedEventsRef.current = new Set(arr.slice(-100));
    }

    const lang = localStorage.getItem("lang") || "en";
    const rtl = lang === "ar";

    // ✅ Event configuration
    const eventConfig = {
      created: {
        title: { en: "New Snag Created", ar: "تم إنشاء ملاحظة جديدة" },
        type: "success",

      },
      updated: {
        title: { en: "Snag Updated", ar: "تم تحديث الملاحظة" },
        type: "info",
   
      },
      fixed: {
        title: { en: "Snag Fixed", ar: "تم إصلاح الملاحظة" },
        type: "success",
      
      },
      rectified: {
        title: { en: "Snag Rectified", ar: "تم تصحيح الملاحظة" },
        type: "success",
   
      },
      verified: {
        title: { en: "Snag Verified", ar: "تم التحقق من الملاحظة" },
        type: "success",
      
      },
      closed: {
        title: { en: "Snag Closed", ar: "تم إغلاق الملاحظة" },
        type: "success",
    
      },
      reopened: {
        title: { en: "Snag Reopened", ar: "تم إعادة فتح الملاحظة" },
        type: "warning", // will map to toast.warn safely
   
      },
      disputed: {
        title: { en: "Snag Disputed", ar: "تم الاعتراض على الملاحظة" },
        type: "warning", // ✅ was missing; add something safe
   
      },
    };

    const config = eventConfig[eventType];

    if (!config) {
      console.warn("⚠️ Unknown event type:", eventType);
      dispatch(clearSnagEvent());
      return;
    }

    console.log("🎨 Displaying toast notification", { toastType: config.type });

    const CustomToast = () => (
      <div className={`snag-toast-container ${rtl ? "rtl" : "ltr"}`}>
        <button
          className="snag-custom-close-btn"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            toast.dismiss(eventIdentifier);
          }}
          aria-label="Close"
          type="button"
        >
          ✕
        </button>

        <div className="snag-toast-content">
          <div className="snag-toast-header">
            <span className="snag-toast-icon">{config.icon}</span>
            <span className="snag-toast-title">{config.title[rtl ? "ar" : "en"]}</span>
          </div>

          <div className="snag-toast-number">{latestSnagEvent.snag_number}</div>

          {latestSnagEvent.project?.name && (
            <div className="snag-toast-project">
              {/* <span className="snag-toast-project-icon">📍</span> */}
              <span className="snag-toast-project-label">
                {rtl ? "المشروع:" : "Project:"}
              </span>
              <span className="snag-toast-project-name">
                {latestSnagEvent.project.name}
              </span>
            </div>
          )}

          {latestSnagEvent.severity && (
            <div className="snag-toast-severity">
              <span className={`severity-badge ${latestSnagEvent.severity.toLowerCase()}`}>
                {latestSnagEvent.severity}
              </span>
            </div>
          )}

          {/* {latestSnagEvent.submittedBy && (
            <div className="snag-toast-footer">
              <span className="snag-toast-submitted-label">
                {rtl ? "تم التقديم بواسطة:" : "Reported by:"}
              </span>
              <span className="snag-toast-submitted-by">
                {latestSnagEvent.reported_by_name || ""}
              </span>
            </div>
          )} */}
        </div>
      </div>
    );

    // ✅ Safely pick the correct toast function
    const showToast = getToastFn(config.type);

    showToast(<CustomToast />, {
      toastId: eventIdentifier,
      rtl,
      autoClose: 7000,
      position: "top-right",
      closeButton: false,
      pauseOnHover: true,
      draggable: true,
      hideProgressBar: false,
      className: "snag-toast-custom",
      bodyClassName: "snag-toast-body",
      progressClassName: "snag-toast-progress",
      closeOnClick: false,
    });

    console.log("✅ Toast notification displayed");

    dispatch(clearSnagEvent());
  }, [latestSnagEvent, eventType, dispatch]);

  return null;

}